// Per-chat registry of still-running sub-agents that survived past
// their spawn tool's return. Sub-agents land here when the parent's
// spawn_subagent or spawn_batch tool is interrupted by a user steer
// mid-execute: the spawn returns early with a handle id, the child
// keeps running, the supervisor tools (list_subagents,
// steer_subagent, abort_subagent, await_subagent) operate by id
// against this registry.
//
// A registry is owned per Agent. Created in piChat.js's
// createChatAgent and threaded into the spawn + supervisor tool
// factories via closure.
//
// Handle shape:
//   {
//     kind: "subagent" | "batch" | "reader" (an expand's reader leaf, present only while it runs),
//     child: Agent | null,            // single-child case
//     promise: Promise<RunResult>,    // resolves when child(ren) finish
//     state: object,                  // live state slot (still mutates)
//     taskSummary: string,
//     abort: () => void,
//   }

export function createChildHandleRegistry() {
    const byId = new Map();
    const listeners = new Set();

    function notify() {
        for (const l of listeners) {
            try { l(); } catch { /* listener errors must not break the registry */ }
        }
    }

    return {
        add(id, handle) {
            byId.set(id, handle);
            notify();
        },
        remove(id) {
            const removed = byId.delete(id);
            if (removed) notify();
            return removed;
        },
        get(id) {
            return byId.get(id) || null;
        },
        list() {
            return Array.from(byId, ([id, h]) => ({
                id,
                kind: h.kind || "subagent",
                taskSummary: h.taskSummary || "",
                status: h.state?.status || "running",
            }));
        },
        entries() {
            return Array.from(byId);
        },
        size() {
            return byId.size;
        },
        subscribe(cb) {
            listeners.add(cb);
            return () => listeners.delete(cb);
        },
        // Called by spawn tools when a registered handle's live state
        // object mutates (e.g. on every child agent event after the
        // tool returned). Wakes the same listener set as add/remove —
        // subscribers (typically ChatConsole) re-read state and push
        // it into chat.items so the rendered tool keeps updating
        // post-interrupt.
        notifyStateChange(id) {
            if (byId.has(id)) notify();
        },
    };
}
